import { Block } from '@/components/Block';
import { BlockSet } from '@/components/BlockSet';
import Box from '@/components/Box';
import {
  DEFAULT_CIRCLE_PROBABILITY,
  DEFAULT_COLOR_VARIETY,
  DEFAULT_QUARTER_CIRCLE_PROBABILITY,
  DEFAULT_RECTANGLE_PROBABILITY,
  DEFAULT_SAME_COLOR_ADJACENCY_WEIGHT,
} from '@/constants/blockGrid';
import { BlockColors, BlockShapes } from '@/types/blocks';
import React, { memo, useState } from 'react';

interface Props {}

const Blocks: React.FC<Props> = memo(() => {
  const [showTable, setShowTable] = useState(false);
  const [gridWidth, setGridWidth] = useState(4);
  const [gridHeight, setGridHeight] = useState(4);
  const [seed, setSeed] = useState(Math.floor(Math.random() * 100));

  const [circleProbability, setCircleProbability] = useState(
    DEFAULT_CIRCLE_PROBABILITY,
  );
  const [quarterCircleProbability, setQuarterCircleProbability] = useState(
    DEFAULT_QUARTER_CIRCLE_PROBABILITY,
  );
  const [rectangleProbability, setRectangleProbability] = useState(
    DEFAULT_RECTANGLE_PROBABILITY,
  );
  const [colorVariety, setColorVariety] = useState(DEFAULT_COLOR_VARIETY);
  const [sameColorAdjacencyWeight, setSameColorAdjacencyWeight] = useState(
    DEFAULT_SAME_COLOR_ADJACENCY_WEIGHT,
  );

  // Function to adjust other probabilities when one changes
  const adjustProbability = (
    type: 'circle' | 'quarterCircle' | 'rectangle',
    newValue: number,
  ) => {
    // Ensure the new value is between 0 and 1
    newValue = Math.max(0, Math.min(1, newValue));

    if (type === 'circle') {
      setCircleProbability(newValue);

      // Calculate remaining probability to distribute
      const remaining = 1 - newValue;

      // Get current ratio between quarter circle and rectangle
      const currentSum = quarterCircleProbability + rectangleProbability;
      if (currentSum === 0) {
        // If both are 0, distribute evenly
        setQuarterCircleProbability(remaining / 2);
        setRectangleProbability(remaining / 2);
      } else {
        // Maintain the ratio between quarter circle and rectangle
        const quarterCircleRatio = quarterCircleProbability / currentSum;
        const rectangleRatio = rectangleProbability / currentSum;

        setQuarterCircleProbability(remaining * quarterCircleRatio);
        setRectangleProbability(remaining * rectangleRatio);
      }
    } else if (type === 'quarterCircle') {
      setQuarterCircleProbability(newValue);

      // Calculate remaining probability to distribute
      const remaining = 1 - newValue;

      // Get current ratio between circle and rectangle
      const currentSum = circleProbability + rectangleProbability;
      if (currentSum === 0) {
        // If both are 0, distribute evenly
        setCircleProbability(remaining / 2);
        setRectangleProbability(remaining / 2);
      } else {
        // Maintain the ratio between circle and rectangle
        const circleRatio = circleProbability / currentSum;
        const rectangleRatio = rectangleProbability / currentSum;

        setCircleProbability(remaining * circleRatio);
        setRectangleProbability(remaining * rectangleRatio);
      }
    } else if (type === 'rectangle') {
      setRectangleProbability(newValue);

      // Calculate remaining probability to distribute
      const remaining = 1 - newValue;

      // Get current ratio between circle and quarter circle
      const currentSum = circleProbability + quarterCircleProbability;
      if (currentSum === 0) {
        // If both are 0, distribute evenly
        setCircleProbability(remaining / 2);
        setQuarterCircleProbability(remaining / 2);
      } else {
        // Maintain the ratio between circle and quarter circle
        const circleRatio = circleProbability / currentSum;
        const quarterCircleRatio = quarterCircleProbability / currentSum;

        setCircleProbability(remaining * circleRatio);
        setQuarterCircleProbability(remaining * quarterCircleRatio);
      }
    }
  };

  return (
    <Box>
      <h1 className="text-2xl font-bold mb-4">Blocks Showcase</h1>

      <div className="mb-8 space-y-6">
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">Random Block Grid</h2>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Grid dimensions and seed */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Grid Settings</h3>

                <div className="flex flex-col space-y-2">
                  <label htmlFor="width-slider" className="text-sm font-medium">
                    Width: {gridWidth}
                  </label>
                  <input
                    id="width-slider"
                    type="range"
                    min="1"
                    max="10"
                    value={gridWidth}
                    onChange={(e) => setGridWidth(Number(e.target.value))}
                    className="w-full"
                  />
                </div>

                <div className="flex flex-col space-y-2">
                  <label
                    htmlFor="height-slider"
                    className="text-sm font-medium"
                  >
                    Height: {gridHeight}
                  </label>
                  <input
                    id="height-slider"
                    type="range"
                    min="1"
                    max="10"
                    value={gridHeight}
                    onChange={(e) => setGridHeight(Number(e.target.value))}
                    className="w-full"
                  />
                </div>

                <div className="flex flex-col space-y-2">
                  <label htmlFor="seed-slider" className="text-sm font-medium">
                    Seed: {seed}
                  </label>
                  <input
                    id="seed-slider"
                    type="range"
                    min="1"
                    max="100"
                    value={seed}
                    onChange={(e) => setSeed(Number(e.target.value))}
                    className="w-full"
                  />
                </div>
              </div>

              {/* Shape probabilities */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Shape Probabilities</h3>

                <div className="flex flex-col space-y-2">
                  <label
                    htmlFor="circle-prob-slider"
                    className="text-sm font-medium"
                  >
                    Circle: {circleProbability.toFixed(2)}
                  </label>
                  <input
                    id="circle-prob-slider"
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={circleProbability}
                    onChange={(e) =>
                      adjustProbability('circle', Number(e.target.value))
                    }
                    className="w-full"
                  />
                </div>

                <div className="flex flex-col space-y-2">
                  <label
                    htmlFor="quarter-circle-prob-slider"
                    className="text-sm font-medium"
                  >
                    Quarter Circle: {quarterCircleProbability.toFixed(2)}
                  </label>
                  <input
                    id="quarter-circle-prob-slider"
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={quarterCircleProbability}
                    onChange={(e) =>
                      adjustProbability('quarterCircle', Number(e.target.value))
                    }
                    className="w-full"
                  />
                </div>

                <div className="flex flex-col space-y-2">
                  <label
                    htmlFor="rectangle-prob-slider"
                    className="text-sm font-medium"
                  >
                    Rectangle: {rectangleProbability.toFixed(2)}
                  </label>
                  <input
                    id="rectangle-prob-slider"
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={rectangleProbability}
                    onChange={(e) =>
                      adjustProbability('rectangle', Number(e.target.value))
                    }
                    className="w-full"
                  />
                </div>

                <div className="flex flex-col space-y-2">
                  <label htmlFor="total-prob" className="text-sm font-medium">
                    Total:{' '}
                    {(
                      circleProbability +
                      quarterCircleProbability +
                      rectangleProbability
                    ).toFixed(2)}
                  </label>
                  <div className="h-2 w-full bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500"
                      style={{
                        width: `${(circleProbability + quarterCircleProbability + rectangleProbability) * 100}%`,
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Color settings */}
            <div className="space-y-4 mt-4">
              <h3 className="text-lg font-medium">Color Settings</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col space-y-2">
                  <label
                    htmlFor="color-variety-slider"
                    className="text-sm font-medium"
                  >
                    Color Variety: {colorVariety}
                  </label>
                  <input
                    id="color-variety-slider"
                    type="range"
                    min="1"
                    max={BlockColors.filter((c) => c !== 'black').length}
                    step="1"
                    value={colorVariety}
                    onChange={(e) => setColorVariety(Number(e.target.value))}
                    className="w-full"
                  />
                </div>

                <div className="flex flex-col space-y-2">
                  <label
                    htmlFor="adjacency-weight-slider"
                    className="text-sm font-medium"
                  >
                    Same Color Adjacency: {sameColorAdjacencyWeight.toFixed(2)}
                  </label>
                  <input
                    id="adjacency-weight-slider"
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={sameColorAdjacencyWeight}
                    onChange={(e) =>
                      setSameColorAdjacencyWeight(Number(e.target.value))
                    }
                    className="w-full"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="p-4 border rounded-md bg-gray-50">
            <BlockSet
              width={gridWidth}
              height={gridHeight}
              seed={seed}
              circleProbability={circleProbability}
              quarterCircleProbability={quarterCircleProbability}
              rectangleProbability={rectangleProbability}
              colorVariety={colorVariety}
              sameColorAdjacencyWeight={sameColorAdjacencyWeight}
            />
          </div>
        </div>

        <div>
          <button
            onClick={() => setShowTable(!showTable)}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            {showTable ? 'Hide' : 'Show'} All Blocks Table
          </button>
        </div>
      </div>

      {showTable && (
        <div className="overflow-x-auto">
          <table className="border-collapse border mb-8">
            <thead>
              <tr>
                <th className="border p-2">Shape / Color</th>
                {BlockColors.map((color) => (
                  <th key={color} className="border p-2 capitalize">
                    {color}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {BlockShapes.map((shapeConfig, index) => (
                <tr key={index}>
                  <td className="border border-gray-300 p-2 font-medium">
                    {shapeConfig.shape === 'quarter-circle'
                      ? `${shapeConfig.shape} (${shapeConfig.orientation})`
                      : shapeConfig.shape}
                  </td>
                  {BlockColors.map((color) => (
                    <td
                      key={color}
                      className={'border border-gray-300 p-4 text-center'}
                    >
                      <div className="flex justify-center">
                        <Block
                          block={{
                            color,
                            shape: shapeConfig,
                          }}
                        />
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Box>
  );
});
Blocks.displayName = 'Blocks';

export default Blocks;
